import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UsuariosApi, ColaboradoresApi } from "../api/client.js";
import { useState, useMemo, useEffect } from "react";
import { useIsMobile } from "../hooks/useIsMobile.js";
import { Table } from "../components/Table.jsx";
import { useToast } from "../components/ToastProvider.jsx";
import { Modal } from "../components/Modal.jsx";
import { PasswordInput } from "../components/PasswordInput.jsx";

export default function UsuariosPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: resp, isLoading } = useQuery({
    queryKey: ["usuarios"],
    queryFn: () => UsuariosApi.listar(),
  });
  const usuarios = resp?.data || resp || [];
  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 10;

  const totalPaginas = useMemo(() => {
    if (!Array.isArray(usuarios) || usuarios.length === 0) return 1;
    return Math.ceil(usuarios.length / itensPorPagina);
  }, [usuarios]);

  const usuariosPaginados = useMemo(() => {
    const inicio = (paginaAtual - 1) * itensPorPagina;
    const fim = inicio + itensPorPagina;
    return usuarios.slice(inicio, fim);
  }, [usuarios, paginaAtual]);

  useEffect(() => {
    if (paginaAtual > totalPaginas) setPaginaAtual(totalPaginas);
  }, [paginaAtual, totalPaginas]);

  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [perfil, setPerfil] = useState("COLABORADOR");
  const [autoColab, setAutoColab] = useState(true);
  const [modalDeletar, setModalDeletar] = useState({
    open: false,
    id: null,
    nome: "",
  });
  const [modalEditar, setModalEditar] = useState({
    open: false,
    id: null,
    nome: "",
    email: "",
    perfil: "",
    ativo: true,
    senha: "",
  });
  const criar = useMutation({
    mutationFn: async () => {
      const u = await UsuariosApi.criar({ nome, email, senha, perfil });
      if (autoColab && perfil === "COLABORADOR") {
        try {
          await ColaboradoresApi.criar({
            usuarioId: u.id,
            cargo: "Não Definido",
            setor: "Geral",
          });
        } catch (e) {
          toast.error("Usuário criado mas falha ao criar colaborador");
        }
      }
      return u;
    },
    onSuccess: () => {
      toast.success("Usuário criado");
      setNome("");
      setEmail("");
      setSenha("");
      setPerfil("COLABORADOR");
      qc.invalidateQueries({ queryKey: ["usuarios"] });
      qc.invalidateQueries({ queryKey: ["colaboradores"] });
    },
    onError: (e) => toast.error(e?.response?.data?.message || "Erro ao criar"),
  });

  const deletar = useMutation({
    mutationFn: (id) => UsuariosApi.deletar(id),
    onSuccess: () => {
      toast.success("Usuário deletado com sucesso");
      qc.invalidateQueries({ queryKey: ["usuarios"] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.message || "Erro ao deletar"),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, dados }) => UsuariosApi.atualizar(id, dados),
    onSuccess: () => {
      toast.success("Usuário atualizado com sucesso");
      qc.invalidateQueries({ queryKey: ["usuarios"] });
      setModalEditar({ open: false, id: null, nome: "", email: "", perfil: "", ativo: true, senha: "" });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.message || "Erro ao atualizar"),
  });

  const deleteUser = (id, nome) => {
    setModalDeletar({ open: true, id, nome });
  };

  const editUser = (id) => {
    const usuario = usuarios.find(u => u.id === id);
    if (usuario) {
      setModalEditar({
        open: true,
        id: usuario.id,
        nome: usuario.nome,
        email: usuario.email,
        perfil: usuario.perfil,
        ativo: usuario.ativo,
        senha: "",
      });
    }
  };

  const confirmarEdicaoUser = () => {
    const { id, nome, perfil, ativo, senha } = modalEditar;
    const dados = { nome, perfil, ativo };
    if (senha?.trim()) dados.senha = senha;
    atualizar.mutate({ 
      id, 
      dados
    });
  };

  const confirmarDeletar = () => {
    deletar.mutate(modalDeletar.id);
    setModalDeletar({ open: false, id: null, nome: "" });
  };

  const columns = [
    {
      key: "_stack",
      header: "Usuário",
      render: (_, row) => (
        <div className="stack-group">
          <div className="stack-name">{row.nome}</div>
          <div className="stack-email">{row.email}</div>
          <div className="stack-line">
            <label>Perfil: </label>
            <span className="stack-perfil">{row.perfil}</span>
          </div>
          <div className="stack-line">
            <label>Ativo: </label>
            <span>{row.ativo ? "Sim" : "Não"}</span>
          </div>
        </div>
      ),
    },
    { key: "nome", header: "Nome" },
    { key: "email", header: "Email" },
    { key: "perfil", header: "Perfil" },
    { key: "ativo", header: "Ativo", render: (v) => (v ? "Sim" : "Não") },
    {
      key: "action",
      header: "Ações",
      render: (_, row) => (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: ".2rem",
          }}
        >
          <button
            className="btn"
            onClick={() => editUser(row.id)}
            style={{
              fontSize: ".7rem",
              padding: ".10rem .5rem",
              background: "#007bff",
            }}
          >
            Editar
          </button>
          <button
            className="btn"
            onClick={() => deleteUser(row.id, row.nome)}
            style={{
              fontSize: ".7rem",
              padding: ".10rem .5rem",
              background: "#dc3545",
            }}
          >
            Excluir
          </button>
        </div>
      ),
    },
  ];

  const isMobile = useIsMobile();

  return (
    <div>
      <h2 className="page-title">Usuários</h2>
      <div className="card" style={{ marginBottom: "1rem" }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            criar.mutate();
          }}
          className="flex gap-sm"
          style={{ flexWrap: "wrap" }}
        >
          <input
            placeholder="Nome"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            required
            style={{ flex: "1 1 160px" }}
          />
          <input
            placeholder="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ flex: "1 1 200px" }}
          />
          <PasswordInput
            placeholder="Senha"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            required
            style={{ flex: "1 1 140px" }}
          />
          <select
            value={perfil}
            onChange={(e) => setPerfil(e.target.value)}
            style={{ flex: "1 1 140px" }}
          >
            {["COLABORADOR", "LIDER", "RH", "GERENTE"].map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>
          <button className="btn" disabled={criar.isPending}>
            Criar
          </button>
          {perfil === "COLABORADOR" && (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: "0.5rem",
              }}
            >
              <input
                type="checkbox"
                checked={autoColab}
                onChange={(e) => setAutoColab(e.target.checked)}
                style={{ width: "auto" }}
              />
              <label style={{ fontSize: ".7rem" }}>
                Adicionar a lista de colaboradores?
              </label>
            </div>
          )}
        </form>
      </div>
      <div className="card">
        {isLoading ? (
          <p>Carregando...</p>
        ) : isMobile ? (
          <div className="collab-cards">
            {usuariosPaginados.map((u) => (
              <div className="collab-card" key={u.id}>
                <div className="collab-card-header">
                  {u.nome || "(sem nome)"}
                </div>
                <table className="mini-table">
                  <tbody>
                    <tr>
                      <th>Email</th>
                      <td>{u.email}</td>
                    </tr>
                    <tr>
                      <th>Perfil</th>
                      <td>{u.perfil}</td>
                    </tr>
                    <tr>
                      <th>Ativo</th>
                      <td>{u.ativo ? "Sim" : "Não"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
            {!usuariosPaginados.length && (
              <p style={{ fontSize: ".7rem" }}>Nenhum registro</p>
            )}
          </div>
        ) : (
          <Table
            className="table-stack"
            columns={columns}
            data={usuariosPaginados}
            keyField="id"
          />
        )}
        {!isLoading && usuarios.length > 0 && (
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:".5rem", marginTop:".75rem", flexWrap:"wrap"}}>
            <p className="muted" style={{fontSize:".7rem", margin:0}}>
              Exibindo {(paginaAtual - 1) * itensPorPagina + 1} a {Math.min(paginaAtual * itensPorPagina, usuarios.length)} de {usuarios.length}
            </p>
            <div style={{display:"flex", gap:".4rem", flex:"1 1 auto", justifyContent:"center"}}>
              <button
                type="button"
                className="btn"
                onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))}
                disabled={paginaAtual === 1}
                aria-label="Página anterior"
                title="Página anterior"
                style={{fontSize:".7rem", padding:".25rem .5rem"}}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M15 6L9 12L15 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => setPaginaAtual((p) => Math.min(totalPaginas, p + 1))}
                disabled={paginaAtual >= totalPaginas}
                aria-label="Próxima página"
                title="Próxima página"
                style={{fontSize:".7rem", padding:".25rem .5rem"}}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
      <Modal
        open={modalDeletar.open}
        title="Confirmar Exclusão"
        onClose={() => setModalDeletar({ open: false, id: null, nome: "" })}
        actions={
          <>
            <button
              className="btn"
              onClick={() =>
                setModalDeletar({ open: false, id: null, nome: "" })
              }
              style={{ background: "#6c757d" }}
            >
              Cancelar
            </button>
            <button
              className="btn"
              onClick={confirmarDeletar}
              style={{ background: "#dc3545" }}
            >
              Excluir
            </button>
          </>
        }
      >
        <p>
          Tem certeza que deseja excluir o usuário{" "}
          <strong>{modalDeletar.nome}</strong>?
        </p>
        <p style={{ fontSize: ".8rem", color: "#6c757d" }}>
          Esta ação não pode ser desfeita.
        </p>
      </Modal>
      <Modal
        open={modalEditar.open}
        title="Editar Usuário"
        onClose={() => setModalEditar({ open: false, id: null, nome: "", email: "", perfil: "", ativo: true, senha: "" })}
        actions={
          <>
            <button
              className="btn"
              onClick={() =>
                setModalEditar({ open: false, id: null, nome: "", email: "", perfil: "", ativo: true, senha: "" })
              }
              style={{ background: "#6c757d" }}
            >
              Cancelar
            </button>
            <button
              className="btn"
              onClick={confirmarEdicaoUser}
              style={{ background: "#007bff" }}
              disabled={atualizar.isPending}
            >
              Salvar
            </button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div>
            <label style={{ display: "block", marginBottom: ".25rem", fontSize: ".9rem" }}>Nome</label>
            <input
              value={modalEditar.nome}
              onChange={(e) => setModalEditar({ ...modalEditar, nome: e.target.value })}
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: ".25rem", fontSize: ".9rem" }}>Email</label>
            <input
              value={modalEditar.email}
              disabled
              style={{ width: "100%", background: "#f0f0f0", cursor: "not-allowed" }}
              title="Email não pode ser alterado"
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: ".25rem", fontSize: ".9rem" }}>Perfil</label>
            <select
              value={modalEditar.perfil}
              onChange={(e) => setModalEditar({ ...modalEditar, perfil: e.target.value })}
              style={{ width: "100%" }}
            >
              {["COLABORADOR", "LIDER", "RH", "GERENTE"].map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: "block", marginBottom: ".25rem", fontSize: ".9rem" }}>Nova senha (opcional)</label>
            <PasswordInput
              value={modalEditar.senha}
              onChange={(e) => setModalEditar({ ...modalEditar, senha: e.target.value })}
              placeholder="Deixe em branco para manter"
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
            <input
              type="checkbox"
              checked={modalEditar.ativo}
              onChange={(e) => setModalEditar({ ...modalEditar, ativo: e.target.checked })}
              style={{ width: "auto" }}
            />
            <label style={{ fontSize: ".9rem" }}>Usuário ativo</label>
          </div>
        </div>
      </Modal>
    </div>
  );
}
